"use client";

import * as React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { resolveImageAlt } from "@/lib/images/resolve-image-alt";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselPrevious,
	CarouselNext,
	CarouselDots,
	type CarouselApi,
} from "@/ui/components/ui/carousel";

/** Mobile: shorter than 4/5 full-width (~500px @400px) to reduce empty band / LCP box; desktop unchanged. */
const galleryAspectClassName =
	"w-full overflow-hidden rounded-lg bg-secondary aspect-square max-md:max-h-[min(80vw,50vh)] md:max-h-[60vh] lg:max-h-none";

const mainFrameClassName = cn("relative", galleryAspectClassName);

export interface ImageCarouselImage {
	url: string;
	alt?: string | null;
}

interface ImageCarouselProps {
	images: ImageCarouselImage[];
	productName: string;
	/** Show navigation arrows (default: true on desktop) */
	showArrows?: boolean;
	/** Show dot indicators (default: true on mobile) */
	showDots?: boolean;
	/** Show thumbnail strip (default: true on desktop) */
	showThumbnails?: boolean;
	/** Callback when active index changes */
	onIndexChange?: (index: number) => void;
	/** Callback when image is tapped/clicked (for lightbox integration) */
	onImageClick?: (index: number) => void;
	/** Callback when a thumbnail is clicked (after scrolling to that slide; for lightbox) */
	onThumbnailClick?: (index: number) => void;
	/** Additional class name for the container */
	className?: string;
}

/**
 * Image carousel with mobile swipe support.
 *
 * Features:
 * - Horizontal swipe on mobile (Embla Carousel)
 * - Arrow navigation on desktop (hover to reveal)
 * - Thumbnail strip on desktop
 * - Dot indicators on mobile
 * - First image uses preload + high/eager priority for LCP optimization
 *
 * Optional `onImageClick` / `onThumbnailClick` integrate with a lightbox (see ProductGallery).
 */
export function ImageCarousel({
	images,
	productName,
	showArrows = true,
	showDots = true,
	showThumbnails = true,
	onIndexChange,
	onImageClick,
	onThumbnailClick,
	className,
}: ImageCarouselProps) {
	const [api, setApi] = React.useState<CarouselApi>();
	const [selectedIndex, setSelectedIndex] = React.useState(0);

	// Reset to first image when images array changes (e.g., variant switch).
	// The state reset happens during render (react.dev pattern, no extra commit);
	// the imperative embla scroll stays in an effect because it touches the DOM.
	const imagesKey = images.map((img) => img.url).join(",");
	const [prevImagesKey, setPrevImagesKey] = React.useState(imagesKey);
	if (imagesKey !== prevImagesKey) {
		setPrevImagesKey(imagesKey);
		setSelectedIndex(0);
	}
	React.useEffect(() => {
		api?.scrollTo(0, true); // true = instant scroll (no animation)
	}, [imagesKey, api]);

	// Sync selected index from carousel API
	React.useEffect(() => {
		if (!api) return;

		const onSelect = () => {
			const index = api.selectedScrollSnap();
			setSelectedIndex(index);
			onIndexChange?.(index);
		};

		api.on("select", onSelect);
		// Set initial index
		onSelect();

		return () => {
			api.off("select", onSelect);
		};
	}, [api, onIndexChange]);

	const scrollToImage = (index: number) => {
		api?.scrollTo(index);
	};

	// Handle empty images (after hooks to satisfy rules of hooks)
	if (!images.length) {
		return (
			<div className={cn("flex items-center justify-center", galleryAspectClassName)}>
				<span className="text-muted-foreground">No image available</span>
			</div>
		);
	}

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			{/* Main Image Carousel */}
			<Carousel
				setApi={setApi}
				opts={{
					align: "start",
					loop: images.length > 1,
				}}
				className="group w-full"
			>
				<div className={mainFrameClassName}>
					<CarouselContent className="ml-0">
						{images.map((image, index) => (
							<CarouselItem key={image.url} className="pl-0">
								<div
									className={cn(mainFrameClassName, onImageClick && "cursor-pointer")}
									onClick={() => onImageClick?.(index)}
								>
									<Image
										src={image.url}
										alt={resolveImageAlt(image.alt, productName)}
										fill
										className="object-contain object-center p-4"
										sizes="(max-width: 768px) 100vw, 50vw"
										preload={index === 0}
										{...(index === 0 ? { fetchPriority: "high" } : {})}
										loading={index === 0 ? "eager" : "lazy"}
									/>
								</div>
							</CarouselItem>
						))}
					</CarouselContent>

					{/* Navigation arrows - hidden on mobile, visible on desktop hover */}
					{showArrows && images.length > 1 && (
						<>
							<CarouselPrevious
								variant="ghost"
								className={cn(
									"left-4 hidden border border-border bg-background opacity-0 shadow-md transition-opacity hover:bg-accent group-hover:opacity-100 md:flex",
									"disabled:opacity-0",
								)}
							/>
							<CarouselNext
								variant="ghost"
								className={cn(
									"right-4 hidden border border-border bg-background opacity-0 shadow-md transition-opacity hover:bg-accent group-hover:opacity-100 md:flex",
									"disabled:opacity-0",
								)}
							/>
						</>
					)}
				</div>

				{/* Dot indicators for mobile */}
				{showDots && images.length > 1 && <CarouselDots className="mt-4 md:hidden" />}
			</Carousel>

			{/* Thumbnail Strip for desktop */}
			{showThumbnails && images.length > 1 && (
				<div className="scrollbar-hide hidden gap-2 overflow-x-auto px-1 py-1 md:flex">
					{images.map((image, index) => (
						<button
							type="button"
							key={image.url}
							onClick={() => {
								scrollToImage(index);
								onThumbnailClick?.(index);
							}}
							className={cn(
								"relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md transition-all",
								selectedIndex === index
									? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
									: "opacity-60 hover:opacity-100",
							)}
						>
							<Image
								src={image.url}
								alt={`${productName} ${index + 1}/${images.length}`}
								fill
								className="object-cover"
								sizes="80px"
							/>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
